import { isPast } from 'date-fns/isPast';
import { isThisMonth } from 'date-fns/isThisMonth';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import { formatCurrencyValue } from '@eika/currency-input';

import type { Account, PaymentLimits } from '../../generated-types';
import { format } from '../../hooks/useTranslation';
import TranslationsConfigType from '../../translations/translationType';

export const doesAmountExceedAccountBalanceAccountBalance = (account: Account, amount: number): boolean => {
    const balance = account.localBalance?.availableBalance ?? account.balance.availableBalance;
    return balance < amount && !account.rights.overdraft;
};

export type PaymentLimitValidationError = 'amountExceedsMaximum' | 'amountExceedsMonthlyMaximum';

export const doesAmountExceedPaymentLimit = (
    amount?: number,
    date?: ZonedDate,
    paymentLimits?: PaymentLimits,
    futurePaymentAmount?: number,
    isRecurring = false,
): PaymentLimitValidationError | null => {
    if (amount === undefined) return null;
    if (!paymentLimits) return null;

    if (amount > paymentLimits.maxTransactionAmount) {
        return 'amountExceedsMaximum';
    }

    const remainderOfMontlyTransactionAmount =
        paymentLimits.maxMonthlyTransactionAmount - (paymentLimits.accumulatedMonthlyTransactionAmount ?? 0);
    const amountWithoutFuturePaymentAmount = amount - (futurePaymentAmount ?? 0);

    const dueDate = date ?? getZonedDate();

    if (
        !isRecurring &&
        (isPast(dueDate) || isThisMonth(dueDate)) &&
        amountWithoutFuturePaymentAmount > remainderOfMontlyTransactionAmount
    ) {
        return 'amountExceedsMonthlyMaximum';
    }

    return null;
};

export const formatLimitAmountError = (
    translated: TranslationsConfigType,
    type: PaymentLimitValidationError | null,
    paymentLimits: PaymentLimits | undefined,
): string | null => {
    if (!type) return null;
    if (!paymentLimits) return null;

    switch (type) {
        case 'amountExceedsMaximum':
            return format(translated.paymentValidation[type], formatCurrencyValue(paymentLimits.maxTransactionAmount));

        case 'amountExceedsMonthlyMaximum':
            return format(
                translated.paymentValidation[type],
                formatCurrencyValue(paymentLimits.maxMonthlyTransactionAmount),
            );

        default:
            return null;
    }
};

export default doesAmountExceedAccountBalanceAccountBalance;
